import { createContext, useCallback, useContext, useReducer } from "react";
import type { ReactNode } from "react";
import type { CardContext } from "@/types/cardContext";
import type { ParticipantRef } from "@/types/participant";
import type { PromoPolicy } from "@/lib/promoPolicy";

// ── Types ────────────────────────────────────────────────────────────────────

export type CaptureStep = "idle" | "capture" | "metadata" | "choice_gate" | "processing" | "done";

export type ProcessingMode = 'full' | 'transcript_only' | 'skip' | null;

export type CaptureType = "voice_moment" | "general_memory" | "photo" | "drawing" | "shortie" | "time_capsule";

export interface CaptureData {
  type: CaptureType | null;

  // Step 1 outputs — type-specific
  audioStoragePath?: string;         // voice/time_capsule: uploaded audio path
  audioDurationSeconds?: number;     // voice/time_capsule: recorded length in seconds
  /** When true, VoiceCapture begins recording on mount (PWA shortcut entry). */
  autoStartRecording?: boolean;
  imageFile?: File;                  // photo/drawing — primary (first) image
  imagePreviewUrl?: string;          // photo/drawing — primary preview URL
  imageFiles?: File[];               // photo — all selected files (multi-photo)
  imagePreviewUrls?: string[];       // photo — all preview URLs (multi-photo)
  voiceCommentPath?: string;         // photo/drawing: uploaded voice comment path
  voiceCommentDuration?: number;
  caption?: string;                  // photo/drawing
  videoBlob?: Blob;                  // shortie
  thumbnailBlob?: Blob;              // shortie
  videoDuration?: number;            // shortie
  videoMimeType?: string;            // shortie
  writtenText?: string;              // general_memory: typed text content
  contextMode?: 'voice' | 'written'; // general_memory: whether user narrated or typed
  contextAudioPath?: string;         // context step: uploaded voice description path
  contextText?: string;              // context step: typed description

  // Step 2 outputs — metadata (shared)
  childIds: string[];
  /** Tagged people (owner / co-parents / contributors). Defaults to owner only. */
  participants: ParticipantRef[];
  location: string | null;
  memoryDate: Date;
  /** `undefined` = never set (legacy / no picker involved) → auto-create from card eligible.
   *  `null` = user explicitly opted out in the metadata picker → skip auto-create.
   *  `string` = chosen collection id. */
  collectionId?: string | null;
  /** Display icon for the active collection — shown in the capture type picker banner. */
  collectionIcon?: string;
  /** Hex colour for the active collection — used to tint the capture type picker banner. */
  collectionColor?: string;
  /** If set, the participant picker is constrained to these connection IDs
   *  (plus the owner). Set when capture starts from a collection that has its
   *  own participant list. Empty/undefined = no restriction. */
  collectionParticipantConnectionIds?: string[];
  /** Time-Capsule lifecycle at capture time. Do NOT confuse with audience (who can see). */
  capsuleState: "default" | "time_capsule";
  /** Who can see the memory. 'space' (default) = Owner + Members + tagged
   *  Contributors. 'members' = Owner + Members only; tagged Contributors lose
   *  access. Set from the "Share with tagged people" toggle on the metadata
   *  screen — shown only when at least one non-member is tagged. */
  audience?: "members" | "space";
  /** Child-only: 'private' hides the memory from family until the child
   *  chooses to share. Omitted for adult/contributor sessions — the DB
   *  trigger resolves the correct default. */
  approvalStatus?: 'pending' | 'private';

  // Card context — survives all steps
  cardContext: CardContext | null;
  contextPrompt?: string;
  voiceRequired?: boolean;

  // AI gate step
  /** AI processing mode selected at the choice gate (or auto-bypassed). */
  processingMode: ProcessingMode;
  /** Promo policy resolved once when the capture step begins. Persists until cancel. */
  promoPolicy: PromoPolicy | null;
  /** ms timestamp when the user manually made their AI choice. null = auto-bypassed. */
  aiChoiceMadeAt: number | null;
}

export interface MemoryMetadata {
  childIds: string[];
  participants: ParticipantRef[];
  location: string | null;
  memoryDate: Date;
  /** Picker value at save time. `undefined` = metadata step did not include a
   *  picker (legacy paths) — preserve the pre-existing collectionId in state.
   *  `null` = user explicitly opted out of any collection. `string` = chosen id. */
  collectionId?: string | null;
  /** Audience at save time — see CaptureData.audience. */
  audience?: "members" | "space";
}

interface CaptureContextValue {
  step: CaptureStep;
  data: CaptureData;
  startCapture: (opts?: {
    type?: CaptureType;
    cardContext?: CardContext;
    contextPrompt?: string;
    voiceRequired?: boolean;
    collectionId?: string;
    collectionIcon?: string;
    collectionColor?: string;
    collectionParticipantConnectionIds?: string[];
    autoStart?: boolean;
  }) => void;
  selectType: (type: CaptureType) => void;
  completeCaptureStep: (result: Partial<CaptureData>) => void;
  completeMetadataStep: (metadata: MemoryMetadata, bypass?: { processingMode: ProcessingMode }) => void;
  /** Explicit opt-out from the active collection for this capture only.
   *  Triggered e.g. from the CaptureTypePicker "Cancel hoarding" dialog. */
  optOutOfActiveCollection: () => void;
  completeProcessing: (memoryId: string) => void;
  cancel: () => void;
  /** Step back in the capture flow. Preserves context (collection/card) but clears
   *  capture outputs so the user can re-record/re-capture. See reducer for
   *  per-step transitions. */
  goBack: () => void;
  /** User made their AI processing choice at the gate. Advances to processing. */
  setProcessingMode: (mode: ProcessingMode) => void;
  /** Store the resolved promo policy once on capture step entry. */
  setPromoPolicy: (policy: PromoPolicy) => void;
  memoryId: string | null;
}

// ── Initial state ────────────────────────────────────────────────────────────

const INITIAL_DATA: CaptureData = {
  type: null,
  childIds: [],
  participants: [],
  location: null,
  memoryDate: new Date(),
  // collectionId intentionally omitted — undefined distinguishes "never set" (eligible
  // for auto-create from a feed card) from null (user explicitly opted out).
  capsuleState: "default",
  cardContext: null,
  contextMode: undefined,
  processingMode: null,
  promoPolicy: null,
  aiChoiceMadeAt: null,
};

// ── Reducer ──────────────────────────────────────────────────────────────────

type Action =
  | { type: "START"; payload: { captureType: CaptureType | null; cardContext: CardContext | null; contextPrompt?: string; voiceRequired?: boolean; collectionId?: string; collectionIcon?: string; collectionColor?: string; collectionParticipantConnectionIds?: string[]; autoStart?: boolean } }
  | { type: "SELECT_TYPE"; payload: CaptureType }
  | { type: "COMPLETE_CAPTURE"; payload: Partial<CaptureData> }
  | { type: "COMPLETE_METADATA"; payload: MemoryMetadata }
  | { type: "OPT_OUT_COLLECTION" }
  | { type: "START_PROCESSING"; payload?: { processingMode?: ProcessingMode } }
  | { type: "COMPLETE_PROCESSING"; payload: string }
  | { type: "SET_PROCESSING_MODE"; payload: { mode: ProcessingMode } }
  | { type: "SET_PROMO_POLICY"; payload: PromoPolicy }
  | { type: "RESET_GATE" }
  | { type: "CANCEL" }
  | { type: "GO_BACK" };

interface State {
  step: CaptureStep;
  data: CaptureData;
  memoryId: string | null;
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "START":
      return {
        step: "capture",
        memoryId: null,
        data: {
          ...INITIAL_DATA,
          type: action.payload.captureType,
          cardContext: action.payload.cardContext,
          contextPrompt: action.payload.contextPrompt,
          voiceRequired: action.payload.voiceRequired,
          collectionId: action.payload.collectionId,
          collectionIcon: action.payload.collectionIcon,
          collectionColor: action.payload.collectionColor,
          collectionParticipantConnectionIds: action.payload.collectionParticipantConnectionIds,
          capsuleState: action.payload.captureType === "time_capsule" ? "time_capsule" : "default",
          memoryDate: new Date(),
          autoStartRecording: action.payload.autoStart,
        },
      };
    case "SELECT_TYPE":
      return {
        ...state,
        data: {
          ...state.data,
          type: action.payload,
          capsuleState: action.payload === "time_capsule" ? "time_capsule" : state.data.capsuleState,
        },
      };
    case "COMPLETE_CAPTURE": {
      const nextData = { ...state.data, ...action.payload };
      return {
        ...state,
        step: "metadata",
        data: nextData,
      };
    }
    case "OPT_OUT_COLLECTION":
      // Explicit opt-out from the active collection mid-capture (e.g. the
      // CaptureTypePicker pill dialog). `null` is the explicit "no collection"
      // signal honoured by MemoryMetadataScreen and captureProcessor.
      return {
        ...state,
        data: {
          ...state.data,
          collectionId: null,
          collectionIcon: undefined,
          collectionColor: undefined,
        },
      };
    case "COMPLETE_METADATA":
      return {
        ...state,
        step: "choice_gate",
        data: {
          ...state.data,
          childIds: action.payload.childIds,
          participants: action.payload.participants,
          location: action.payload.location,
          memoryDate: action.payload.memoryDate,
          // collectionId semantics: `undefined` in the payload means the
          // metadata step did not drive a picker (legacy path) — preserve
          // whatever was set at capture start. `null` means the user
          // explicitly opted out; `string` means they picked one. Using
          // `in` rather than `??` so an explicit `null` is respected, and
          // preserving undefined end-to-end lets captureProcessor distinguish
          // "never set" from "opted out" when deciding whether to auto-create.
          collectionId:
            "collectionId" in action.payload
              ? action.payload.collectionId
              : state.data.collectionId,
          audience: action.payload.audience ?? state.data.audience,
        },
      };
    case "START_PROCESSING":
      return {
        ...state,
        step: "processing",
        data: {
          ...state.data,
          ...(action.payload?.processingMode !== undefined
            ? { processingMode: action.payload.processingMode }
            : {}),
        },
      };
    case "COMPLETE_PROCESSING":
      return { ...state, step: "done", memoryId: action.payload };
    case "SET_PROCESSING_MODE":
      return {
        ...state,
        step: "processing",
        data: {
          ...state.data,
          processingMode: action.payload.mode,
          aiChoiceMadeAt: Date.now(),
        },
      };
    case "SET_PROMO_POLICY":
      return {
        ...state,
        data: { ...state.data, promoPolicy: action.payload },
      };
    case "RESET_GATE":
      return {
        ...state,
        step: "metadata",
        data: { ...state.data, processingMode: null },
      };
    case "CANCEL":
      return { step: "idle", data: { ...INITIAL_DATA, memoryDate: new Date() }, memoryId: null };
    case "GO_BACK": {
      // choice_gate → metadata (clears processingMode, keeps promoPolicy)
      if (state.step === "choice_gate") {
        return {
          ...state,
          step: "metadata",
          data: { ...state.data, processingMode: null },
        };
      }

      // metadata → capture (retake), capture-with-type → capture (back to type picker).
      // processing has no back — user committed. idle/capture-no-type/done = no-op.
      const clearedCapture: Partial<CaptureData> = {
        audioStoragePath: undefined,
        audioDurationSeconds: undefined,
        imageFile: undefined,
        imagePreviewUrl: undefined,
        imageFiles: undefined,
        imagePreviewUrls: undefined,
        voiceCommentPath: undefined,
        voiceCommentDuration: undefined,
        caption: undefined,
        videoBlob: undefined,
        thumbnailBlob: undefined,
        videoDuration: undefined,
        videoMimeType: undefined,
        writtenText: undefined,
        contextMode: undefined,
        contextAudioPath: undefined,
        contextText: undefined,
        processingMode: null,
      };
      if (state.step === "metadata") {
        return {
          ...state,
          step: "capture",
          data: { ...state.data, ...clearedCapture },
        };
      }
      if (state.step === "capture" && state.data.type) {
        return {
          ...state,
          data: { ...state.data, type: null, ...clearedCapture },
        };
      }
      return state;
    }
    default:
      return state;
  }
}

// ── Context ──────────────────────────────────────────────────────────────────

const CaptureContext = createContext<CaptureContextValue | null>(null);

export function CaptureProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    step: "idle",
    data: { ...INITIAL_DATA, memoryDate: new Date() },
    memoryId: null,
  });

  const startCapture = useCallback((opts?: {
    type?: CaptureType;
    cardContext?: CardContext;
    contextPrompt?: string;
    voiceRequired?: boolean;
    collectionId?: string;
    collectionIcon?: string;
    collectionColor?: string;
    collectionParticipantConnectionIds?: string[];
    autoStart?: boolean;
  }) => {
    dispatch({
      type: "START",
      payload: {
        captureType: opts?.type ?? null,
        cardContext: opts?.cardContext ?? null,
        contextPrompt: opts?.contextPrompt,
        voiceRequired: opts?.voiceRequired,
        collectionId: opts?.collectionId,
        collectionIcon: opts?.collectionIcon,
        collectionColor: opts?.collectionColor,
        collectionParticipantConnectionIds: opts?.collectionParticipantConnectionIds,
        autoStart: opts?.autoStart,
      },
    });
  }, []);

  const selectType = useCallback((type: CaptureType) => {
    dispatch({ type: "SELECT_TYPE", payload: type });
  }, []);

  const completeCaptureStep = useCallback((result: Partial<CaptureData>) => {
    dispatch({ type: "COMPLETE_CAPTURE", payload: result });
  }, []);

  const optOutOfActiveCollection = useCallback(() => {
    dispatch({ type: "OPT_OUT_COLLECTION" });
  }, []);

  const completeMetadataStep = useCallback((metadata: MemoryMetadata, bypass?: { processingMode: ProcessingMode }) => {
    dispatch({ type: "COMPLETE_METADATA", payload: metadata });
    if (bypass) {
      // React 18 auto-batches both dispatches — user skips choice_gate entirely.
      dispatch({ type: "START_PROCESSING", payload: { processingMode: bypass.processingMode } });
    }
  }, []);

  const completeProcessing = useCallback((memoryId: string) => {
    dispatch({ type: "COMPLETE_PROCESSING", payload: memoryId });
  }, []);

  const cancel = useCallback(() => {
    dispatch({ type: "CANCEL" });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: "GO_BACK" });
  }, []);

  const setProcessingMode = useCallback((mode: ProcessingMode) => {
    dispatch({ type: "SET_PROCESSING_MODE", payload: { mode } });
  }, []);

  const setPromoPolicy = useCallback((policy: PromoPolicy) => {
    dispatch({ type: "SET_PROMO_POLICY", payload: policy });
  }, []);

  return (
    <CaptureContext.Provider
      value={{
        step: state.step,
        data: state.data,
        memoryId: state.memoryId,
        startCapture,
        selectType,
        completeCaptureStep,
        completeMetadataStep,
        optOutOfActiveCollection,
        completeProcessing,
        cancel,
        goBack,
        setProcessingMode,
        setPromoPolicy,
      }}
    >
      {children}
    </CaptureContext.Provider>
  );
}

export function useCaptureContext(): CaptureContextValue {
  const ctx = useContext(CaptureContext);
  if (!ctx) throw new Error("useCaptureContext must be used within CaptureProvider");
  return ctx;
}
